import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Input,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  useWorkspaceCustomSkillCreate,
  useWorkspaceCustomSkillDelete,
  useWorkspaceCustomSkillDetail,
  useWorkspaceCustomSkills,
  useWorkspaceCustomSkillUpdate,
} from "../services/api";
import {
  WorkspaceCustomSkillDetail,
  WorkspaceCustomSkillRead,
} from "../common/types";
import {
  composeCustomSkillMarkdown,
  parseCustomSkillMarkdown,
} from "../views/workspaceCustomSkills";

interface SkillDraft {
  id: string;
  fileName: string;
  markdown: string;
  name: string;
  description: string;
  snippet: string;
  parseError: string | null;
  saved: boolean;
}

interface WorkspaceCustomSkillsProps {
  workspaceId: string;
  isAdmin: boolean;
}

const apiErrorMessage = (cause: unknown, fallback: string): string =>
  cause instanceof Error ? cause.message : fallback;

const WorkspaceCustomSkills = ({
  workspaceId,
  isAdmin,
}: WorkspaceCustomSkillsProps) => {
  const listSkills = useWorkspaceCustomSkills();
  const getSkill = useWorkspaceCustomSkillDetail();
  const createSkill = useWorkspaceCustomSkillCreate();
  const updateSkill = useWorkspaceCustomSkillUpdate();
  const deleteSkill = useWorkspaceCustomSkillDelete();
  const uploadRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const [skills, setSkills] = useState<WorkspaceCustomSkillRead[]>([]);
  const [drafts, setDrafts] = useState<SkillDraft[]>([]);
  const [draftIndex, setDraftIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<WorkspaceCustomSkillDetail | null>(
    null,
  );
  const [editMarkdown, setEditMarkdown] = useState("");
  const [deleteTarget, setDeleteTarget] =
    useState<WorkspaceCustomSkillRead | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setSkills(await listSkills(workspaceId));
      setError(null);
    } catch {
      setError("Custom skills could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [listSkills, workspaceId]);

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin, refresh]);

  const currentDraft = drafts[draftIndex];
  const currentNameError = currentDraft
    ? skills.some(
        (skill) =>
          skill.status === "active" && skill.name === currentDraft.name,
      )
      ? "A custom skill with this name already exists in this workspace."
      : drafts.some(
            (draft) =>
              draft.id !== currentDraft.id && draft.name === currentDraft.name,
          )
        ? "Each selected file must have a different skill name."
        : null
    : null;
  const pendingDrafts = useMemo(
    () => drafts.filter((draft) => !draft.saved),
    [drafts],
  );

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (files.length > 10) {
      setError("Select no more than ten skill files at a time.");
      return;
    }
    if (files.some((file) => !file.name.toLowerCase().endsWith(".md"))) {
      setError("Only .md files can be added as custom skills.");
      return;
    }

    const parsedDrafts = await Promise.all(
      files.map(async (file, index): Promise<SkillDraft> => {
        const markdown = await file.text();
        try {
          const parsed = parseCustomSkillMarkdown(markdown);
          return {
            id: `${file.name}-${index}-${Date.now()}`,
            fileName: file.name,
            markdown,
            name: parsed.name,
            description: parsed.description,
            snippet: parsed.snippet,
            parseError: null,
            saved: false,
          };
        } catch (cause) {
          return {
            id: `${file.name}-${index}-${Date.now()}`,
            fileName: file.name,
            markdown,
            name: "",
            description: "",
            snippet: "",
            parseError:
              cause instanceof Error ? cause.message : "This file is invalid.",
            saved: false,
          };
        }
      }),
    );
    setDrafts(parsedDrafts);
    setDraftIndex(0);
    setError(null);
    setNotice(null);
  };

  const saveDraft = async () => {
    if (!currentDraft || currentDraft.parseError || currentNameError) return;
    setSaving(true);
    setError(null);
    try {
      const markdown = composeCustomSkillMarkdown(
        currentDraft.markdown,
        currentDraft.name,
      );
      await createSkill(workspaceId, markdown);
      const updatedDrafts = drafts.map((draft) =>
        draft.id === currentDraft.id
          ? { ...draft, markdown, saved: true }
          : draft,
      );
      setDrafts(updatedDrafts);
      const nextIndex = updatedDrafts.findIndex((draft) => !draft.saved);
      if (nextIndex >= 0) setDraftIndex(nextIndex);
      await refresh();
      setNotice(
        nextIndex >= 0
          ? "Skill saved. Review the next unsaved file."
          : "All selected skills were saved.",
      );
    } catch (cause) {
      setError(apiErrorMessage(cause, "The skill could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const openEditor = async (skill: WorkspaceCustomSkillRead) => {
    setSaving(true);
    setError(null);
    try {
      const detail = await getSkill(workspaceId, skill.id);
      setEditing(detail);
      setEditMarkdown(detail.markdown);
    } catch {
      setError("The selected custom skill could not be opened.");
    } finally {
      setSaving(false);
    }
  };

  const replaceMarkdown = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".md")) {
      setError("Choose a .md file to replace this skill.");
      return;
    }
    const markdown = await file.text();
    try {
      parseCustomSkillMarkdown(markdown);
      setEditMarkdown(markdown);
      setError(null);
      setNotice(
        `Loaded ${file.name}. Save the skill to apply the replacement.`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The replacement file is invalid.",
      );
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      await updateSkill(workspaceId, editing.id, editMarkdown, editing.version);
      setEditing(null);
      setNotice("Custom skill saved.");
      await refresh();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The custom skill could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const disableSkill = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await deleteSkill(workspaceId, deleteTarget.id);
      setDeleteTarget(null);
      setNotice(`${deleteTarget.name} was removed from this workspace.`);
      await refresh();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The custom skill could not be removed.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Card variant="outlined" sx={{ p: 3 }} id="custom-skills">
      <Stack spacing={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="h3">Custom skills</Typography>
            <Chip size="sm" color="warning" variant="soft">
              Beta
            </Chip>
          </Stack>
          <Typography level="body-sm" color="neutral">
            Add workspace-only instruction skills from Markdown files. Workspace
            admins can edit or remove them.
          </Typography>
        </Box>
        {error && <Alert color="danger">{error}</Alert>}
        {notice && <Alert color="success">{notice}</Alert>}
        <Box>
          <input
            ref={uploadRef}
            type="file"
            accept=".md,text/markdown"
            multiple
            hidden
            aria-label="Select Markdown skill files"
            onChange={(event) => void handleFiles(event)}
          />
          <Button onClick={() => uploadRef.current?.click()} disabled={saving}>
            Add Markdown skills
          </Button>
        </Box>

        {pendingDrafts.length > 0 && currentDraft && (
          <Card variant="soft" color="neutral">
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography level="title-md">
                  Review skill {draftIndex + 1} of {drafts.length}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={draftIndex === 0}
                    onClick={() => setDraftIndex((index) => index - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={draftIndex >= drafts.length - 1}
                    onClick={() => setDraftIndex((index) => index + 1)}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
              <Typography level="body-sm">{currentDraft.fileName}</Typography>
              {currentDraft.parseError ? (
                <Alert color="danger">{currentDraft.parseError}</Alert>
              ) : (
                <>
                  <Typography level="body-sm">
                    {currentDraft.description}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    Instruction preview
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 1.5,
                      m: 0,
                      maxHeight: 140,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      bgcolor: "background.surface",
                      borderRadius: "sm",
                    }}
                  >
                    {currentDraft.snippet}
                  </Box>
                  <FormSkillName
                    value={currentDraft.name}
                    error={currentNameError}
                    onChange={(name) =>
                      setDrafts((all) =>
                        all.map((draft) =>
                          draft.id === currentDraft.id
                            ? { ...draft, name }
                            : draft,
                        ),
                      )
                    }
                  />
                  {currentDraft.saved && <Alert color="success">Saved</Alert>}
                  <Button
                    onClick={() => void saveDraft()}
                    disabled={
                      saving ||
                      Boolean(currentDraft.parseError) ||
                      Boolean(currentNameError) ||
                      currentDraft.saved
                    }
                  >
                    Save this skill
                  </Button>
                </>
              )}
              {saving && <CircularProgress size="sm" />}
            </Stack>
          </Card>
        )}

        <Divider />
        <Typography level="title-md">Saved skills</Typography>
        {loading ? (
          <CircularProgress size="sm" />
        ) : skills.length === 0 ? (
          <Typography level="body-sm" color="neutral">
            No custom skills have been added to this workspace.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {skills.map((skill) => (
              <Card key={skill.id} variant="outlined" size="sm">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Box>
                    <Typography level="title-sm">{skill.name}</Typography>
                    <Typography level="body-sm" color="neutral">
                      {skill.description}
                    </Typography>
                    {skill.status === "disabled" && (
                      <Typography level="body-xs" color="neutral">
                        Removed
                      </Typography>
                    )}
                  </Box>
                  {skill.status === "active" && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="outlined"
                        disabled={saving}
                        onClick={() => void openEditor(skill)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        disabled={saving}
                        onClick={() => setDeleteTarget(skill)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        )}

        {editing && (
          <Card variant="soft" color="neutral">
            <Stack spacing={1.5}>
              <Typography level="title-md">Edit {editing.name}</Typography>
              <Textarea
                minRows={8}
                value={editMarkdown}
                onChange={(event) => setEditMarkdown(event.target.value)}
                aria-label="Custom skill Markdown"
              />
              <input
                ref={replaceRef}
                type="file"
                accept=".md,text/markdown"
                hidden
                aria-label="Replace skill Markdown file"
                onChange={(event) => void replaceMarkdown(event)}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => replaceRef.current?.click()}
                >
                  Replace with Markdown file
                </Button>
                <Button onClick={() => void saveEdit()} disabled={saving}>
                  Save changes
                </Button>
                <Button variant="plain" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}

        {deleteTarget && (
          <Alert
            color="warning"
            endDecorator={
              <Stack direction="row" spacing={1}>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="danger"
                  onClick={() => void disableSkill()}
                  disabled={saving}
                >
                  Remove skill
                </Button>
              </Stack>
            }
          >
            Remove {deleteTarget.name} from this workspace? It will remain in
            the audit history.
          </Alert>
        )}
      </Stack>
    </Card>
  );
};

const FormSkillName = ({
  value,
  error,
  onChange,
}: {
  value: string;
  error: string | null;
  onChange: (value: string) => void;
}) => (
  <Box>
    <Typography level="body-sm" component="label" htmlFor="custom-skill-name">
      Skill name
    </Typography>
    <Input
      id="custom-skill-name"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={Boolean(error)}
    />
    {error && (
      <Typography level="body-xs" color="danger">
        {error}
      </Typography>
    )}
  </Box>
);

export default WorkspaceCustomSkills;
